import { useState, useEffect, ReactNode } from 'react';
import { Loader2, AlertCircle, GitBranch } from 'lucide-react';
import { api } from '@/api/client';

const STATE_KEY = 'gitea_oauth_state';

export function AuthGate({ children }: { children: ReactNode }) {
  const [authed, setAuthed] = useState(api.hasToken());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle the OAuth redirect back from Gitea (?code=...&state=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code  = params.get('code');
    const state = params.get('state');
    if (!code) return;

    // Verify CSRF state before doing anything else
    const savedState = sessionStorage.getItem(STATE_KEY);
    sessionStorage.removeItem(STATE_KEY);

    if (!savedState || state !== savedState) {
      setError('Security check failed — please try signing in again.');
      window.history.replaceState({}, '', '/');
      return;
    }

    // Clean the URL immediately so a reload doesn't re-submit the code
    window.history.replaceState({}, '', '/');
    setLoading(true);

    api.giteaCallback(code)
      .then(({ token }) => {
        api.setToken(token);
        setAuthed(true);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  async function handleSignIn() {
    setLoading(true);
    setError(null);
    try {
      const { url, state } = await api.giteaAuthUrl();
      sessionStorage.setItem(STATE_KEY, state);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start sign-in');
      setLoading(false);
    }
  }

  if (authed) return <>{children}</>;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <div className="max-w-sm w-full bg-white rounded-xl border border-gray-200 p-8 shadow-sm">

        <div className="mb-8 text-center">
          <h1 className="text-xl font-semibold text-gray-900">ProMed KB</h1>
          <p className="text-sm text-gray-500 mt-1">Support Agent Admin</p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-3 py-4 text-gray-500">
            <Loader2 size={24} className="animate-spin text-brand-600" />
            <p className="text-sm">Signing in…</p>
          </div>
        ) : (
          <button
            onClick={handleSignIn}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            <GitBranch size={18} />
            Sign in with Gitea
          </button>
        )}

        {error && (
          <div className="mt-4 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

      </div>
    </div>
  );
}
